import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/client';
import { useIsAdmin } from '../../api/session';
import type { AuditEvent, Page } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { PageUnavailable, QueryView } from '../../ui/feedback';
import { Input } from '../../ui/forms';
import { LoadMore, PageHeader, usePageTitle } from '../../ui/misc';
import { EventTable } from '../shared/EventTable';

/** AUD-01–03 read-only security and administration audit. */
export function AuditPage() {
  usePageTitle('Security and administration audit');
  useCrumbs([{ label: 'Administration' }, { label: 'Audit' }]);
  const admin = useIsAdmin();
  const [type, setType] = useState('');
  const q = useInfiniteQuery({
    queryKey: ['audit', type],
    queryFn: ({ pageParam }) =>
      api.get<Page<AuditEvent>>(
        `/admin/audit?${new URLSearchParams({ ...(pageParam ? { cursor: pageParam } : {}), ...(type ? { type } : {}) })}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
    enabled: admin,
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="Security and administration audit"
        subtitle="Append-only. Secret values and file contents are never recorded."
      />
      <div className="filter-bar">
        <Input
          aria-label="Filter by event type"
          placeholder="Event type, e.g. authorization.denied"
          value={type}
          onChange={(e) => setType(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>
      <QueryView query={q}>
        {(d) => (
          <>
            <EventTable events={d.pages.flatMap((p) => p.items)} showIds />
            <LoadMore
              hasMore={!!q.hasNextPage}
              onClick={() => q.fetchNextPage()}
              loading={q.isFetchingNextPage}
            />
          </>
        )}
      </QueryView>
    </div>
  );
}
